"use client";

import { faCheck, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Label as PrimitiveLabel, Switch as PrimitiveSwitch } from "radix-ui";
import { type ReactNode, forwardRef } from "react";
import { type FieldValues, type RegisterOptions, useFormContext } from "react-hook-form";
import { cn } from "../../..";
import styles from "./Switch.module.css";

export interface SwitchProps extends PrimitiveSwitch.SwitchProps {
	innerClassName?: string;
	labelPosition?: "left" | "right";
	label?: ReactNode;
	registerOptions?: RegisterOptions<FieldValues>;
	thumbs?: {
		on: string;
		off: string;
	};
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function SelectRef(props, ref) {
	const {
		innerClassName,
		labelPosition = "right",
		label,
		children,
		thumbs,
		className,
		registerOptions,
		...restProps
	} = props;
	const thumbLength = thumbs ? Math.max(thumbs.on.length, thumbs.off.length) + 1 : undefined;

	const methods = useFormContext();

	const registeredProps =
		methods && props.name
			? methods.register(props.name, {
					setValueAs: (val) => val === "true" || val === true,
					...registerOptions,
				})
			: { ref };

	const checked = props.checked ?? (props.name ? methods.watch(props.name) : undefined);

	return (
		<div
			className={cn(
				"flex items-center",
				{
					"flex-row-reverse": labelPosition === "left",
					"opacity-50": props.disabled,
				},
				className,
			)}
		>
			<PrimitiveSwitch.Root
				{...restProps}
				{...registeredProps}
				checked={checked}
				ref={ref}
				onCheckedChange={(checked) => {
					props.onCheckedChange?.(checked);

					if (methods && props.name) {
						methods.setValue(props.name, checked);
					}
				}}
				id={props.id || props.name}
				className={cn(
					styles.root,
					"relative inline-flex h-6 min-w-[2.75rem] shrink-0 items-center rounded-full pr-8 pl-1 outline-none focus-visible:ring-2 focus-visible:ring-offset-1 motion-safe:transition-all",
				)}
			>
				<PrimitiveSwitch.Thumb
					className={cn(
						styles.thumb,
						"flex h-4 items-center justify-center rounded-full bg-white px-1 text-xs leading-none motion-safe:transition-all",
					)}
				>
					<span
						className={cn("items-center justify-center text-primary-medium", styles.thumbOn)}
						style={thumbLength ? { width: `${thumbLength}ch` } : undefined}
					>
						{thumbs?.on ?? <FontAwesomeIcon icon={faCheck} fixedWidth />}
					</span>
					<span
						className={cn("items-center justify-center text-shades-dark", styles.thumbOff)}
						style={thumbLength ? { width: `${thumbLength}ch` } : undefined}
					>
						{thumbs?.off ?? <FontAwesomeIcon icon={faTimes} fixedWidth />}
					</span>
				</PrimitiveSwitch.Thumb>
			</PrimitiveSwitch.Root>

			{(!!label || !!children) && (
				<div
					className={cn(
						"inline-flex items-center gap-2 leading-none",
						{
							"pl-2": labelPosition === "right",
							"pr-2": labelPosition === "left",
						},
						innerClassName,
					)}
				>
					{!!label && (
						<PrimitiveLabel.Root htmlFor={props.id || props.name}>{label}</PrimitiveLabel.Root>
					)}

					{children}
				</div>
			)}
		</div>
	);
});
